/**
 * Retries a startup/first-load network call once after a short delay if it
 * throws (timeout, abort, DNS/connection failure) or returns a transient
 * non-ok response. Staging (and any environment mid-deploy) briefly makes
 * every API 502/timeout for a few seconds while the app pool recycles; a
 * single retry lets first-load providers self-heal instead of leaving the
 * app stuck on default/loading state until a manual reload.
 *
 * Never retries 401/403 — those are real auth failures, not transient ones.
 */
export async function fetchWithStartupRetry(
  fn: () => Promise<Response>,
  retries = 1,
  delayMs = 3000
): Promise<Response> {
  let response: Response | undefined;
  try {
    response = await fn();
  } catch (err) {
    if (retries <= 0) throw err;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return fetchWithStartupRetry(fn, retries - 1, delayMs);
  }

  if (!response.ok && response.status !== 401 && response.status !== 403 && retries > 0) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return fetchWithStartupRetry(fn, retries - 1, delayMs);
  }

  return response;
}
